import {
  Button,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  RadioGroup,
  RadioGroupItem,
} from "@win-reality/react-ui";
import { useState } from "react";

import { TeamSide } from "../../constants/teams";
import { ThemedDialog } from "../ThemedDialog";

type SetHomeTeamDialogProps = {
  homeTeamName: string;
  awayTeamName: string;
  onCancel: () => void;
  /** The side picked as home: "away" swaps the teams */
  onSave: (homeSide: TeamSide) => void;
  /** Shows Save loading while the change saves */
  isSaving?: boolean;
};

/**
 * "Who is the home team?" (the mobile app's Set Home Team), for a game
 * scheduled with its teams the wrong way round.
 */
export const SetHomeTeamDialog = ({
  homeTeamName,
  awayTeamName,
  onCancel,
  onSave,
  isSaving = false,
}: SetHomeTeamDialogProps) => {
  const [homeSide, setHomeSide] = useState<TeamSide>("home");

  const options: { side: TeamSide; name: string }[] = [
    { side: "home", name: homeTeamName },
    { side: "away", name: awayTeamName },
  ];

  return (
    <ThemedDialog
      open
      onOpenChange={(open) => !open && !isSaving && onCancel()}
      defaultCancelButton={false}
      size="sm"
    >
      {({ showDivider, setScrollContainer }) => (
        <>
          <DialogScrollableContent setScrollContainer={setScrollContainer}>
            <DialogHeader>
              <DialogTitle className="text-gray-900 dark:text-white">
                Set Home Team
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-5 pb-2">
              <p className="text-sm text-gray-900 dark:text-white">
                Who is the home team?
              </p>
              <RadioGroup
                value={homeSide}
                onValueChange={(value) => setHomeSide(value as TeamSide)}
                className="flex flex-col gap-4"
              >
                {options.map((option) => (
                  <label
                    key={option.side}
                    className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white"
                  >
                    <RadioGroupItem
                      value={option.side}
                      data-testid="set-home-team-option"
                      data-team={option.side}
                    />
                    {option.name}
                  </label>
                ))}
              </RadioGroup>
            </div>
          </DialogScrollableContent>

          <DialogFooter showDivider={showDivider}>
            <div className="flex w-full items-center justify-between">
              <Button
                variant="ghost"
                onClick={onCancel}
                isDisabled={isSaving}
                data-testid="set-home-team-dialog-cancel-button"
                className="underline"
              >
                Cancel
              </Button>
              <Button
                variant="secondary-flat-light"
                isDisabled={isSaving}
                isLoading={isSaving}
                aria-busy={isSaving}
                onClick={() => onSave(homeSide)}
                data-testid="set-home-team-dialog-confirm-button"
                className="min-w-[136px] rounded-full"
              >
                Save
              </Button>
            </div>
          </DialogFooter>
        </>
      )}
    </ThemedDialog>
  );
};
